"use client"

import { useTranslations } from "@/lib/intl/use-translations"
import {
  MRI_PACKAGES,
  PLAN_BLOOD_ADDONS,
  PLAN_IMAGING_ADDONS,
  PLAN_TIERS,
} from "./pricing-v4-data"

/**
 * All of Pricing v4's words in one place. The page's own copy is the
 * `pricingV4` namespace; the add-on names and one-liners are the live
 * pricing page's (`pricing.addon*`), so both pages say the same thing.
 * Prices are not copy: the client formats them from the live feed.
 */
export function usePricingV4Copy() {
  const t = useTranslations("pricingV4")
  const p = useTranslations("pricing")

  return {
    HERO: {
      eyebrow: t("heroEyebrow"),
    },
    PLAN: {
      eyebrow: t("planEyebrow"),
      heading: t("planHeading"),
      rhythmLabel: t("planRhythmLabel"),
      tiers: PLAN_TIERS.map((tier, i) => {
        const n = i + 1
        return {
          ...tier,
          label: t(`tier${n}Label` as "tier1Label"),
          name: t(`tier${n}Name` as "tier1Name"),
          tests: t(`tier${n}Tests` as "tier1Tests"),
          desc: t(`tier${n}Desc` as "tier1Desc"),
        }
      }),
      perYear: t("perYear"),
      cta: t("planCta"),
      billing: t("planBilling"),
      oneTime: t("planOneTime"),
      oneTimeLink: t("planOneTimeLink"),
      includedHeading: t("includedHeading"),
      included: [1, 2, 3, 4, 5, 6].map((n) => t(`included${n}` as "included1")),
    },
    MRI: {
      eyebrow: t("mriEyebrow"),
      heading: t("mriHeading"),
      body: t("mriBody"),
      packages: MRI_PACKAGES.map((pkg) => {
        const key =
          pkg.id === "core"
            ? "Core"
            : pkg.id === "advanced"
              ? "Advanced"
              : "Pro"
        return {
          ...pkg,
          name: t(`mri${key}Name` as "mriAdvancedName"),
          bullets: Array.from({ length: pkg.bullets }, (_, i) =>
            t(`mri${key}${i + 1}` as "mriAdvanced1")
          ),
        }
      }),
      onTop: t("mriOnTop"),
      // The package's list price carries its own blood test, so the honest
      // comparison is the two totals, not the add-on against the list price.
      total: (total: string, price: string) => t("mriTotal", { total, price }),
      cta: t("mriCta"),
    },
    ADDONS: {
      eyebrow: t("addonsEyebrow"),
      heading: t("addonsHeading"),
      body: t("addonsBody"),
      planPrice: t("addonsPlanPrice"),
      regularPrice: t("addonsRegularPrice"),
      groups: [
        {
          label: t("addonsBloodLabel"),
          items: PLAN_BLOOD_ADDONS.map((a) => ({
            key: a.titleKey,
            name: p(a.titleKey),
            desc: p(a.descKey),
          })),
        },
        {
          label: t("addonsImagingLabel"),
          items: PLAN_IMAGING_ADDONS.map((a) => ({
            key: a.titleKey,
            name: p(a.titleKey),
            desc: p(a.descKey),
          })),
        },
      ],
    },
  }
}
